const { accountType, banks, errors } = require('@transfeeradev/api-enums');
const ValidationError = require('./errors/ValidationError');
const Default = require('./Default');
const ErrorMessageCompiler = require('@transfeeradev/api-services/ErrorMessageCompiler');
const PersonTypeService = require('@transfeeradev/api-services/PersonTypeService');

const ACCOUNT_TYPES = {
  CONTA_CORRENTE: ['01', '02', '03', '05', '11', '12', '13', '14', '29', '71', '77', '92', '48'],
  CONTA_POUPANCA: ['60']
};

const ACCOUNT_BEGINNING_ONLY_CNPJ = ['14', '13', '12', '11'];

module.exports = class Santander extends Default {
  constructor(data) {
    super('Santander', data, banks.SANTANDER.code);
    this._data.agency = this._removeUnnecessaryLeadingZeros(this._data.agency, this._agencySchemaLength);
    this._data.account = this._removeUnnecessaryLeadingZeros(this._data.account, this._accountSchemaLength);
  }

  _validateAgency() {
    super._validateAgency();
    this._data.agency = this._fillZero(this._data.agency, this._agencySchemaLength);
  }

  _validateAccountDigit(account = this._data.account) {
    const aux = new Array('9', '7', '3', '1', '9', '7', '1', '3', '1', '9', '7', '3');
    const agencyAccount = this._data.agency.toString() + account.toString();
    let cont = 0;
    let soma = 0;
    let digito = 0;
    let auxRet = false;
    for (cont = 0; cont < 12; cont++) {
      soma = soma + ((agencyAccount.substring(cont, cont + 1) * aux[cont]) % 10);
    }
    digito = (10 - (soma % 10)) % 10;
    if (digito == agencyAccount.substring(12, 13)) auxRet = true;
    return auxRet;
  }

  _validateAccountType(account = this._data.account) {
    super._validateAccountType();

    const type = account.substr(0, 2);
    return ACCOUNT_TYPES[this._data.account_type].includes(type);
  }

  _validateAccount(inputErrors) {
    super._validateAccount();

    this._data.account = this._data.account + this._data.account_digit;

    const isAccountTypeValid = this._validateAccountType();
    const accountTypeSuggestion = this._getAccountTypeSuggestion();
    const accountBeginning = this._data.account.substr(0, 2);

    if (!isAccountTypeValid && accountTypeSuggestion) {
      const error = errors.ptBr.dba.ACCOUNT_TYPE_3;
      throw new ValidationError(error.message, error.field, accountTypeSuggestion, error.code);
    } else if (!isAccountTypeValid && !accountTypeSuggestion) {
      if (this._tryAccountSuggestion(inputErrors)) {
        return;
      }

      const data = {
        accountBeginning
      };
      const error = new ErrorMessageCompiler(errors.ptBr.dba.INVALID_ACCOUNT_BEGINNING, data).compile();
      throw new ValidationError(error.message, error.field, undefined, error.code, data);
    }

    if (ACCOUNT_BEGINNING_ONLY_CNPJ.includes(accountBeginning) && !PersonTypeService.isCNPJ(this._data.cpf_cnpj)) {
      const error = errors.ptBr.dba.ACCOUNT_IS_JUST_FOR_CNPJ;
      throw new ValidationError(error.message, error.field, null, error.code);
    }

    if (!this._validateAccountDigit()) {
      if (this._tryAccountSuggestion(inputErrors)) {
        return;
      }
      const error = errors.ptBr.dba.AGENCY_ACCOUNT_OR_ACCOUNT_DIGIT_INVALID;
      throw new ValidationError(
        error.message,
        error.field,
        {
          account_digit: this.getAccountDigit()
        },
        error.code
      );
    }
  }

  _tryAccountSuggestion(inputErrors) {
    const accountSuggestion = '0' + this._data.account.substr(0, this._accountSchemaLength);
    if (!this._validateAccountType(accountSuggestion) || !this._validateAccountDigit(accountSuggestion)) {
      return false;
    }
    const error = errors.ptBr.dba.INVALID_ACCOUNT;
    inputErrors.push(
      new ValidationError(
        error.message,
        error.field,
        {
          account: accountSuggestion.substr(0, this._accountSchemaLength)
        },
        error.code
      )
    );
    if (+this._data.account_digit !== +accountSuggestion.substr(this._accountSchemaLength)) {
      const error2 = errors.ptBr.dba.AGENCY_ACCOUNT_OR_ACCOUNT_DIGIT_INVALID;
      inputErrors.push(
        new ValidationError(
          error2.message,
          error2.field,
          {
            account_digit: accountSuggestion.substr(this._accountSchemaLength)
          },
          error2.code
        )
      );
    }
    return true;
  }

  _getAccountTypeSuggestion() {
    const suggestion = this.getAccountType();
    if (suggestion) {
      return {
        account_type: suggestion
      };
    }
  }

  getAccountType() {
    const type = this._data.account.substr(0, 2);

    return ACCOUNT_TYPES.CONTA_CORRENTE.includes(type)
      ? accountType.CONTA_CORRENTE
      : ACCOUNT_TYPES.CONTA_POUPANCA.includes(type)
      ? accountType.CONTA_POUPANCA
      : null;
  }

  getAccountDigit() {
    if (this._data.account.length > this._accountSchemaLength) {
      this._data.account = this._data.account.substr(0, this._accountSchemaLength);
    }

    const multipliers = [9, 7, 3, 1, 0, 0, 9, 7, 1, 3, 1, 9, 7, 3];
    const bankNumber = this._fillZero(this._data.agency, this._agencySchemaLength) + '00' + this._data.account;
    let result = 0;

    for (let i = 0; i < bankNumber.length; i++) {
      let calc = Number(bankNumber[i]) * multipliers[i];
      if (calc > 10) {
        calc = Number(calc.toString().substr(1, 1));
      }
      result += calc;
    }

    if (result > 10) {
      result = Number(result.toString().substr(1, 1));
    }

    if (result === 0) {
      return 0;
    }

    return (result - 10) * -1;
  }

  validate() {
    const errors = [];

    try {
      this._validateBank();
      this._validateAgency();
      this._validateAccount(errors);
      this._validateAccountType();
    } catch (e) {
      if (e instanceof ValidationError) {
        errors.push(e);
      } else {
        throw e;
      }
    }

    return errors;
  }
};
